// UX36a / UX48 / UX49: quill streak tracker.
//
// Lightweight civic-engagement mechanic. A user "finds a quill" by
// voting on QUILL_FIND_THRESHOLD polls in a single day. They keep the
// quill by hitting QUILL_KEEP_THRESHOLD (a lower bar) every subsequent
// day. Miss a day's keep target → the quill is lost (UX47 one-day
// grace still applies). If they lose it and want it back, they must
// hit the full QUILL_FIND_THRESHOLD again.
//
// UX49: state is PER-PUBKEY, keyed by the signed-in user's pubkey. A
// browser can hold quill state for many accounts (ephemeral + Google
// + encrypted-restored, etc.) without them bleeding into each other.
// Sign-out hides the badge; signing back in restores the right one.
//
// Storage shape (LS value):
//   {
//     v: 3,
//     byPubkey: {
//       '<hex pubkey>': {
//         dayCounts: { 'YYYY-MM-DD': n, ... },  // last ~8 days, sparse
//         streak: 3,                            // consecutive keep-days
//         quillFoundOn: '2026-10-23',
//         lastMetThresholdDate: '2026-10-24',
//         lastMilestoneShown: 7,                // UX43
//         frozen: { ... } | null,               // UX47 grace envelope
//       },
//       ...
//     }
//   }
//
// Public API:
//   - loadQuillState(pubkey)      → state for that pubkey, or EMPTY
//   - recordVoteNow(pubkey)       → updates + returns { state, notification }
//   - checkMissedDay(pubkey)      → mount-time quill-loss check
//   - clearQuillState(pubkey?)    → clear one pubkey, or all
//   - todaysVoteCount(state)      → sugar for UI progress indicators
//   - dailyTargetFor(state)       → FIND vs KEEP depending on quill state
//   - QUILL_FIND_THRESHOLD, QUILL_KEEP_THRESHOLD, STREAK_MILESTONES

const LS_QUILL_STATE = 'verdant_quill_state'
const STORAGE_VERSION = 3

// UX48 / UX49 / UX62: dual thresholds.
//   FIND = votes needed in one day to earn a new quill
//   KEEP = votes needed each day thereafter to hold it
// UX62 revision: KEEP dropped from 5 to 1 so the streak increments on
// ANY day with at least one vote (Duolingo-style). The 10-vote find
// threshold stays — the first quill still feels earned — but keeping
// it rewards daily participation at any level rather than punishing a
// quiet day.
export const QUILL_FIND_THRESHOLD = 10
export const QUILL_KEEP_THRESHOLD = 1

// Legacy export name kept for compatibility with existing imports.
// Points at FIND, which is the higher bar; most historical callers
// were asking "what's today's target?" — use dailyTargetFor(state).
export const QUILL_DAILY_THRESHOLD = QUILL_FIND_THRESHOLD

// UX43: celebration milestones.
export const STREAK_MILESTONES = [7, 30, 100, 365]

const DAY_COUNT_CAP = 8

const EMPTY_PUBKEY_STATE = {
  dayCounts: {},
  streak: 0,
  quillFoundOn: null,
  lastMetThresholdDate: null,
  lastMilestoneShown: 0,
  frozen: null,
}

export function emptyQuillState() {
  return { ...EMPTY_PUBKEY_STATE, dayCounts: {} }
}

function ymd(d) {
  const y = d.getUTCFullYear()
  const m = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function todayYmd() { return ymd(new Date()) }

function yesterdayYmd() {
  const d = new Date()
  d.setUTCDate(d.getUTCDate() - 1)
  return ymd(d)
}

function daysBetweenInclusive(start, end) {
  if (!start || !end) return 0
  const a = new Date(start + 'T00:00:00Z').getTime()
  const b = new Date(end + 'T00:00:00Z').getTime()
  return Math.round((b - a) / 86400000) + 1
}

function pruneDayCounts(dayCounts) {
  const keys = Object.keys(dayCounts)
  if (keys.length <= DAY_COUNT_CAP) return dayCounts
  const sorted = keys.sort()
  const kept = sorted.slice(-DAY_COUNT_CAP)
  const out = {}
  for (const k of kept) out[k] = dayCounts[k]
  return out
}

function sanitizePubkeyState(parsed) {
  const dayCounts = {}
  if (parsed?.dayCounts && typeof parsed.dayCounts === 'object') {
    for (const [k, v] of Object.entries(parsed.dayCounts)) {
      if (/^\d{4}-\d{2}-\d{2}$/.test(k) && Number.isInteger(v) && v >= 0) {
        dayCounts[k] = v
      }
    }
  }
  const frozen = parsed?.frozen
  return {
    dayCounts: pruneDayCounts(dayCounts),
    streak: Number.isInteger(parsed?.streak) ? parsed.streak : 0,
    quillFoundOn: typeof parsed?.quillFoundOn === 'string' ? parsed.quillFoundOn : null,
    lastMetThresholdDate: typeof parsed?.lastMetThresholdDate === 'string'
      ? parsed.lastMetThresholdDate : null,
    lastMilestoneShown: Number.isInteger(parsed?.lastMilestoneShown)
      ? parsed.lastMilestoneShown : 0,
    frozen: (frozen && typeof frozen === 'object'
      && Number.isInteger(frozen.streak)
      && typeof frozen.quillFoundOn === 'string'
      && typeof frozen.frozenOn === 'string')
      ? {
          streak: frozen.streak,
          quillFoundOn: frozen.quillFoundOn,
          daysHeld: Number.isInteger(frozen.daysHeld) ? frozen.daysHeld : frozen.streak,
          frozenOn: frozen.frozenOn,
        }
      : null,
  }
}

// Load the whole byPubkey map. Pre-v3 shape (single state object with
// no per-pubkey keying) can't be attached to any current pubkey, so
// we discard it — the user restarts their quill under the new rules.
function loadAll() {
  try {
    const raw = localStorage.getItem(LS_QUILL_STATE)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (parsed?.v !== STORAGE_VERSION) return {}
    if (!parsed.byPubkey || typeof parsed.byPubkey !== 'object') return {}
    const out = {}
    for (const [pk, s] of Object.entries(parsed.byPubkey)) {
      if (typeof pk === 'string' && pk.length > 0) {
        out[pk] = sanitizePubkeyState(s)
      }
    }
    return out
  } catch {
    return {}
  }
}

function saveAll(byPubkey) {
  try {
    localStorage.setItem(
      LS_QUILL_STATE,
      JSON.stringify({ v: STORAGE_VERSION, byPubkey }),
    )
  } catch { /* quota — noop */ }
}

// One-time migration: if the stored shape is pre-v3 (single global
// state object, not keyed by pubkey) and we have a pubkey to attribute
// it to, attach that state to the pubkey entry. Keeps users who
// earned a quill under UX48 from losing it the moment they sign in
// under UX49.
function migratePreV3IfPresent(pubkey) {
  try {
    const raw = localStorage.getItem(LS_QUILL_STATE)
    if (!raw) return
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return
    if (parsed.v === STORAGE_VERSION) return   // already current
    if (parsed.byPubkey) return                 // already keyed
    const migrated = sanitizePubkeyState(parsed)
    saveAll({ [pubkey]: migrated })
  } catch { /* noop */ }
}

// UX62: one-time quill restore for users whose quill was cleared under
// the strict 5/day keep threshold BEFORE UX62 dropped it to 1/day. We
// restore when:
//   • no current quillFoundOn (so this isn't someone who already has
//     a quill — we never overwrite)
//   • no frozen envelope (user isn't mid-rescue)
//   • at least one vote recorded in today's or yesterday's dayCounts
//     (user is actively engaged, their quill loss was mechanical, not
//     from genuine disengagement)
//   • not yet migrated (v62Restored flag)
// New users with empty dayCounts don't qualify — the 10-vote find bar
// still has to be earned honestly.
// UX65 (revised in UX66): count DISTINCT days with at least one
// recorded vote in dayCounts within the retention window. Used by the
// UX62 restore + UX65 upgrade so a user with multi-day voting history
// doesn't get flattened to streak=1 just because the mechanic reset
// them. The window is DAY_COUNT_CAP (8) days.
//
// We deliberately count distinct days (not consecutive) because
// PollPage votes weren't recorded in dayCounts until UX63 shipped —
// a user whose primary interaction was shared poll links will have
// gaps in dayCounts even for days they actually voted. Counting
// consecutive-only was punishing those users for a bug we created.
// Counting distinct is slightly over-generous but honest about the
// data uncertainty.
function distinctVoteDaysInWindow(dayCounts) {
  if (!dayCounts) return 0
  let count = 0
  for (const n of Object.values(dayCounts)) {
    if (Number.isInteger(n) && n >= 1) count += 1
  }
  return count
}

function maybeRestoreUx62(pubkey, state) {
  if (!state) return state
  // The previous version of this gate (shipped in ad4bf19) incorrectly
  // set v62Restored even when no restore had happened — permanently
  // locking out users who hadn't voted recently at that moment. The
  // flag check below is bypassed when quillFoundOn is still null, so
  // affected users get a second chance after this ship.
  if (state.v62Restored && state.quillFoundOn) return state
  // Already holding a quill or mid-rescue → no action.
  if (state.quillFoundOn || state.frozen) return state

  const today = todayYmd()
  const yesterday = yesterdayYmd()
  const dc = state.dayCounts || {}
  const recentVotes = (dc[today] || 0) + (dc[yesterday] || 0)
  if (recentVotes < 1) {
    // No recent voting — don't gift a quill they haven't been working
    // toward. Don't set the flag either; the next load after they vote
    // will re-run the gate and restore then.
    return state
  }

  // UX65 / UX66: streak reflects distinct days with votes in dayCounts.
  // Not strictly "consecutive" because PollPage votes pre-UX63 silently
  // dropped out of dayCounts, so a gap in the recorded data doesn't
  // necessarily mean a gap in engagement.
  const historyStreak = distinctVoteDaysInWindow(dc)
  const restoredStreak = Math.max(1, state.streak || 0, historyStreak)

  const restored = {
    ...state,
    quillFoundOn: today,
    // lastMetThresholdDate stays "today" so today's additional votes
    // are no-ops (already crossed). Tomorrow's first vote bumps to
    // restoredStreak + 1 via the regular path.
    lastMetThresholdDate: today,
    streak: restoredStreak,
    v62Restored: true,
  }
  saveQuillState(pubkey, restored)
  return restored
}

// UX65: one-time streak upgrade for users who were restored by the
// earlier UX62 logic that flattened everyone to streak=1. If they
// have consecutive-day voting history in dayCounts, grant that as the
// streak. Idempotent via v65Upgraded flag.
// UX66: re-run the upgrade when v65Upgraded was set under the old
// "consecutive days only" rule. We detect that by checking whether
// v66Upgraded is false — a new flag so this fires exactly once per
// pubkey, independent of the earlier v65 pass.
function maybeUpgradeUx66(pubkey, state) {
  if (!state) return state
  if (!state.quillFoundOn) return state         // not currently holding a quill
  if (!state.v62Restored) return state           // never went through UX62 restore
  if (state.v66Upgraded) return state           // already upgraded under distinct-days
  const historyStreak = distinctVoteDaysInWindow(state.dayCounts)
  const better = Math.max(state.streak || 0, historyStreak)
  const upgraded = { ...state, streak: better, v65Upgraded: true, v66Upgraded: true }
  saveQuillState(pubkey, upgraded)
  return upgraded
}

export function loadQuillState(pubkey) {
  if (!pubkey) return emptyQuillState()
  migratePreV3IfPresent(pubkey)
  const all = loadAll()
  const state = all[pubkey] ? all[pubkey] : emptyQuillState()
  const restored = maybeRestoreUx62(pubkey, state)
  return maybeUpgradeUx66(pubkey, restored)
}

function saveQuillState(pubkey, state) {
  if (!pubkey) return
  const all = loadAll()
  all[pubkey] = state
  saveAll(all)
}

export function clearQuillState(pubkey) {
  try {
    if (!pubkey) {
      localStorage.removeItem(LS_QUILL_STATE)
      return
    }
    const all = loadAll()
    delete all[pubkey]
    saveAll(all)
  } catch { /* noop */ }
}

// UX49 helper: today's target depends on whether the user already
// holds a quill (or is being graced via UX47 freeze).
export function dailyTargetFor(state) {
  if (!state) return QUILL_FIND_THRESHOLD
  if (state.quillFoundOn) return QUILL_KEEP_THRESHOLD
  if (state.frozen) return QUILL_KEEP_THRESHOLD
  return QUILL_FIND_THRESHOLD
}

// Convenience read for UI progress indicators.
export function todaysVoteCount(state) {
  if (!state) return 0
  return state.dayCounts?.[todayYmd()] || 0
}

// Called from handleVote right after the publish succeeds. Shape:
//   { state, notification }
// notification is one of:
//   null                                       → nothing to show
//   { type: 'found' }                          → crossed FIND today
//                                                while not holding one
//   { type: 'milestone', days: N }             → streak hit 7/30/100/365
//   { type: 'saved', streak, daysHeld }        → UX47 rescue completed
export function recordVoteNow(pubkey) {
  if (!pubkey) return { state: emptyQuillState(), notification: null }
  const state = loadQuillState(pubkey)
  const today = todayYmd()
  const yesterday = yesterdayYmd()
  const target = dailyTargetFor(state)

  const dayCounts = { ...state.dayCounts }
  const priorTodayCount = dayCounts[today] || 0
  const newTodayCount = priorTodayCount + 1
  dayCounts[today] = newTodayCount
  const pruned = pruneDayCounts(dayCounts)

  // Below the day's target — accounting only.
  if (newTodayCount < target) {
    const next = { ...state, dayCounts: pruned }
    saveQuillState(pubkey, next)
    return { state: next, notification: null }
  }

  // Crossing the target more than once per day is a no-op.
  if (priorTodayCount >= target) {
    const next = { ...state, dayCounts: pruned }
    saveQuillState(pubkey, next)
    return { state: next, notification: null }
  }

  // UX47 rescue takes precedence over the regular find/continue path.
  if (state.frozen && state.frozen.frozenOn === today) {
    const restoredStreak = state.frozen.streak + 1
    const restored = {
      dayCounts: pruned,
      streak: restoredStreak,
      quillFoundOn: state.frozen.quillFoundOn,
      lastMetThresholdDate: today,
      lastMilestoneShown: state.lastMilestoneShown || 0,
      frozen: null,
    }
    const nextMilestone = STREAK_MILESTONES
      .filter((m) => restoredStreak >= m && m > (state.lastMilestoneShown || 0))
      .pop()
    let notification = {
      type: 'saved',
      streak: restoredStreak,
      daysHeld: state.frozen.daysHeld,
    }
    if (nextMilestone) {
      restored.lastMilestoneShown = nextMilestone
      notification = { type: 'milestone', days: nextMilestone }
    }
    saveQuillState(pubkey, restored)
    return { state: restored, notification }
  }

  // Regular path: just crossed today's target.
  const continues = state.lastMetThresholdDate === yesterday
  const newStreak = continues ? state.streak + 1 : 1
  const next = {
    dayCounts: pruned,
    streak: newStreak,
    quillFoundOn: state.quillFoundOn || today,
    lastMetThresholdDate: today,
    lastMilestoneShown: state.lastMilestoneShown || 0,
    frozen: null,
  }

  let notification = null
  if (!state.quillFoundOn) {
    notification = { type: 'found' }
  }

  const nextMilestone = STREAK_MILESTONES
    .filter((m) => newStreak >= m && m > (state.lastMilestoneShown || 0))
    .pop()
  if (nextMilestone) {
    next.lastMilestoneShown = nextMilestone
    notification = { type: 'milestone', days: nextMilestone }
  }

  saveQuillState(pubkey, next)
  return { state: next, notification }
}

// Mount-time quill-loss check. Freezes a UX47 grace envelope if the
// user had a quill but let yesterday's keep target slip. Returns null
// when no action is needed.
export function checkMissedDay(pubkey) {
  if (!pubkey) return null
  const state = loadQuillState(pubkey)
  const today = todayYmd()
  const yesterday = yesterdayYmd()

  // UX47: stale grace envelope — only valid for the day it was frozen.
  if (state.frozen && state.frozen.frozenOn !== today) {
    const cleared = { ...state, frozen: null }
    saveQuillState(pubkey, cleared)
    return null
  }

  if (!state.quillFoundOn) return null
  if (!state.lastMetThresholdDate) return null

  // Still on track: last met today or yesterday. Yesterday's row keeps
  // the quill alive through TODAY — they have until midnight to hit
  // today's keep target.
  if (state.lastMetThresholdDate === today
      || state.lastMetThresholdDate === yesterday) {
    return null
  }

  const daysHeld = daysBetweenInclusive(state.quillFoundOn, state.lastMetThresholdDate)
  const next = {
    ...state,
    streak: 0,
    quillFoundOn: null,
    lastMetThresholdDate: state.lastMetThresholdDate,
    frozen: {
      streak: state.streak,
      quillFoundOn: state.quillFoundOn,
      daysHeld,
      frozenOn: today,
    },
  }
  saveQuillState(pubkey, next)
  return { daysHeld, canSave: true, streakWas: state.streak }
}
